// Where a cart run is in the availability pipeline, from the stages its builds stamped.
// Pure, so the page and its tests read the same verdict.
import type { AvailabilityStageOut, ReachCityHealth } from "../api/generated/model";
import { parseTs } from "../api/labels";

export type StageState =
  | { kind: "scraping" }
  | { kind: "built" }
  | { kind: "failed"; step: string; error: string }
  | { kind: "late"; minutes: number }
  | { kind: "waiting" };

const LIVE = new Set(["queued", "claimed", "running"]);

export function stageState(
  stage: AvailabilityStageOut,
  now: Date,
  lagMinutes: number,
): StageState {
  if (LIVE.has(stage.cart_status) || stage.scrape_finished_at === null) return { kind: "scraping" };
  if (stage.reach_built_at !== null) return { kind: "built" };
  if (stage.last_error !== null) {
    return { kind: "failed", step: stage.last_error_step ?? "build", error: stage.last_error };
  }
  const minutes = (now.getTime() - parseTs(stage.scrape_finished_at).getTime()) / 60_000;
  if (minutes > lagMinutes) return { kind: "late", minutes: Math.floor(minutes) };
  return { kind: "waiting" };
}

// Seconds from one stage to the next, or null when either has not happened.
export function delaySeconds(from: string | null, to: string | null): number | null {
  if (from === null || to === null) return null;
  return (parseTs(to).getTime() - parseTs(from).getTime()) / 1000;
}

// The platform's worst state, for its tab: a failure outranks lateness. Null with no cart
// runs, which have nothing to be in any state about.
export function platformState(
  stages: readonly AvailabilityStageOut[],
  now: Date,
  lagMinutes: number,
): StageState["kind"] | null {
  if (stages.length === 0) return null;
  const kinds = new Set(stages.map((s) => stageState(s, now, lagMinutes).kind));
  for (const kind of ["failed", "late", "waiting", "scraping"] as const) {
    if (kinds.has(kind)) return kind;
  }
  return "built";
}

// A city's share of cells the run observed and, of the known cells, the share available.
export function cityShares(row: ReachCityHealth): { observed: number; available: number | null } {
  const known = row.available_cells + row.unavailable_cells;
  return {
    observed: row.cells === 0 ? 0 : row.cells_observed / row.cells,
    available: known === 0 ? null : row.available_cells / known,
  };
}
